/**
 * cataractPipeline — vertical water walls with backlit caustic beams.
 *
 * A series of vertical sheets of water (like a multi-tier fountain)
 * with strong backlighting that creates caustic beams radiating
 * outward. Each sheet is a thin vertical strip of water its
 * surface broken into droplets and ripples.
 *
 * Innovations:
 *   - Water sheets are rendered as columns of 2D SDF (vertical
 *     streaks) with a translucent "thickness" varying with depth.
 *   - Backlighting: a hidden light source behind each sheet creates
 *     caustic beams that radiate outward from the sheet's vertical
 *     axis. The beams are sampled with multiplicative noise so
 *     they appear to flicker.
 *   - Each sheet has a different submersion depth (water level),
 *     creating a stepped cascade.
 *   - Misting: a fine particle field around the sheets, denser
 *     near the sheet axis.
 *   - The sheets overlap slightly, creating regions of "double
 *     illumination" where beams from two sheets add.
 *
 * Audio:
 *   - bass: water flow rate (more mist)
 *   - mid:  beam intensity
 *   - high: sheet rippling (faster undulation)
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CataractPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — dark moody blue.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.02, 0.04, 0.10),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Five vertical water sheets at different x positions.
  // Each sheet is a thin vertical line with a rippling boundary.
  const int SHEETS = 5;
  for (int i = 0; i < SHEETS; i++) {
    float fi = float(i);
    // x position evenly spaced.
    float sx = (fi - 2.0) * 0.30;
    // Sheet thickness (with depth-based gradient).
    float thickness = 0.02 + 0.005 * sin(fi);
    // Sheet vertical extent — varies per sheet.
    float yTop = 0.55 - 0.06 * fi;
    float yBot = -0.55 - 0.04 * fi;
    // Sheet profile: thin vertical strip with horizontal ripple.
    float ripple = 0.008 * sin(p.y * 25.0 + uTime * 3.0 + fi) +
                   0.005 * sin(p.y * 40.0 - uTime * 5.0 + fi * 1.7);
    float sheetD = abs(p.x - (sx + ripple)) - thickness;
    // Mask: only inside the sheet's vertical extent.
    float yMask = step(yBot, p.y) * step(p.y, yTop);
    float sheetMask = (1.0 - smoothstep(0.0, 0.005, sheetD)) * yMask;

    // Sheet color — cool aqua with vertical gradient.
    vec3 sheetColor = mix(
      vec3(0.55, 0.85, 0.95),
      vec3(0.20, 0.55, 0.85),
      (p.y - yBot) / (yTop - yBot)
    );
    col += sheetColor * sheetMask * 0.8;

    // Backlight beam — radiates from the sheet's axis.
    // The beam is a radial gradient from the sheet.
    float beamD = abs(p.x - sx);
    // Beam widens with distance.
    float beamAngle = atan(p.y, p.x - sx);
    // Beam intensity falls off with distance.
    float beam = exp(-beamD * 2.5) * (0.5 + 0.5 * sin(beamAngle * 6.0 + uTime * 2.0));
    // Modulate by noise to make it look like a real caustic.
    beam *= 0.5 + 0.5 * valueNoise(vec2(p.x * 4.0, p.y * 6.0 + uTime));
    // Color: warm light.
    vec3 beamColor = vec3(1.0, 0.85, 0.55);
    col += beamColor * beam * (0.4 + 0.6 * mid);

    // Mist particles around the sheet.
    float mist = 0.0;
    for (int j = 0; j < 4; j++) {
      float fj = float(j);
      vec2 mp = vec2(
        sx + sin(uTime * 0.5 + fj * 1.7) * 0.08,
        mix(yBot, yTop, fract(uTime * 0.15 + fj * 0.21))
      );
      float md = length(p - mp);
      mist += exp(-md * 35.0) * 0.3;
    }
    col += vec3(0.9, 0.95, 1.0) * mist * (0.3 + 0.6 * bass);
  }

  // Submerged base — at the bottom of the screen, water gathers.
  float baseY = -0.55;
  float base = smoothstep(0.03, 0.0, abs(p.y - baseY));
  col += vec3(0.30, 0.55, 0.85) * base * 0.5;
  // Surface ripples on the base.
  float baseRipple = valueNoise(vec2(p.x * 30.0 + uTime * 2.0, 0.0));
  col += vec3(0.6, 0.85, 1.0) * base * baseRipple * 0.4;

  // Splash particles at the bottom of each sheet.
  for (int i = 0; i < SHEETS; i++) {
    float fi = float(i);
    float sx = (fi - 2.0) * 0.30;
    for (int j = 0; j < 3; j++) {
      float fj = float(j);
      float x = sx + (hash21(vec2(fi, fj)) - 0.5) * 0.08;
      float y = -0.50 + hash21(vec2(fi, fj + 5.0)) * 0.2;
      float d = length(p - vec2(x, y));
      col += vec3(1.0, 0.95, 0.8) * exp(-d * 200.0) * 0.7;
    }
  }

  // Caustic network — the interference pattern from multiple beams.
  float caustics = 0.0;
  for (int i = 0; i < SHEETS; i++) {
    float fi = float(i);
    float sx = (fi - 2.0) * 0.30;
    float d = abs(p.x - sx);
    // Use a high-frequency sine to fake interference.
    caustics += exp(-d * 5.0) * (0.5 + 0.5 * sin(p.y * 30.0 - uTime * 5.0 + fi * 1.7));
  }
  col += vec3(0.9, 0.95, 1.0) * caustics * 0.15;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeCataractBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeCataractRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCataractPipeline(gl: WebGL2RenderingContext): CataractPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCataract(
  gl: WebGL2RenderingContext,
  pipe: CataractPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
